'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface TimelinePhase {
  id: number;
  title: string;
  timeframe: string;
  description: string;
  icon: string;
  activities: string[];
  color: string;
}

const TimelineSection = () => {
  const [activePhase, setActivePhase] = useState<number>(1);

  const phases: TimelinePhase[] = [
    {
      id: 1,
      title: 'Založení plánu',
      timeframe: 'Měsíc 1-2',
      description: 'Vytvoříme komplexní základ vašeho finančního plánu na základě první schůzky a důkladné analýzy.',
      icon: 'RocketLaunchIcon',
      activities: [
        'Detailní analýza současné situace',
        'Definování krátkodobých a dlouhodobých cílů',
        'Vytvoření základní strategie',
        'Nastavení monitorovacích metrik',
      ],
      color: 'primary',
    },
    {
      id: 2,
      title: 'Implementace',
      timeframe: 'Měsíc 3-6',
      description: 'Začínáme realizovat dohodnutou strategii a sledujeme první výsledky.',
      icon: 'CogIcon',
      activities: [
        'Zahájení investičních aktivit',
        'Optimalizace pojištění',
        'Nastavení automatických úspor',
        'První kontrolní schůzka',
      ],
      color: 'secondary',
    },
    {
      id: 3,
      title: 'Pravidelné revize',
      timeframe: 'Každé 3-6 měsíců',
      description: 'Pravidelně se setkáváme, vyhodnocujeme pokrok a upravujeme plán podle aktuální situace.',
      icon: 'ArrowPathIcon',
      activities: [
        'Vyhodnocení dosažených výsledků',
        'Úprava strategie podle změn',
        'Optimalizace portfolia',
        'Aktualizace cílů',
      ],
      color: 'accent',
    },
    {
      id: 4,
      title: 'Životní změny',
      timeframe: 'Kdykoliv je potřeba',
      description: 'Reagujeme okamžitě na významné životní události a přizpůsobujeme plán novým okolnostem.',
      icon: 'BoltIcon',
      activities: [
        'Změna zaměstnání nebo příjmu',
        'Rodinné události (svatba, děti)',
        'Koupě nemovitosti',
        'Neočekávané situace',
      ],
      color: 'primary',
    },
    {
      id: 5,
      title: 'Dlouhodobý růst',
      timeframe: 'Roky 2+',
      description: 'Váš plán se vyvíjí spolu s vámi, neustále optimalizujeme a přizpůsobujeme strategii.',
      icon: 'ChartBarIcon',
      activities: [
        'Komplexní roční vyhodnocení',
        'Strategické plánování dalších let',
        'Pokročilá optimalizace',
        'Příprava na důchod',
      ],
      color: 'secondary',
    },
  ];

  const getColorClasses = (color: string, isActive: boolean) => {
    const colors = {
      primary: {
        bg: isActive ? 'bg-primary' : 'bg-primary/10',
        text: isActive ? 'text-white' : 'text-primary',
        border: 'border-primary',
      },
      secondary: {
        bg: isActive ? 'bg-secondary' : 'bg-secondary/10',
        text: isActive ? 'text-white' : 'text-secondary',
        border: 'border-secondary',
      },
      accent: {
        bg: isActive ? 'bg-accent' : 'bg-accent/10',
        text: isActive ? 'text-accent-foreground' : 'text-accent',
        border: 'border-accent',
      },
    };
    return colors[color as keyof typeof colors] || colors.primary;
  };

  return (
    <section id="timeline" className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-text-primary mb-4 font-headline">
              Jak se váš plán vyvíjí v čase
            </h2>
            <p className="text-lg text-text-secondary max-w-2xl mx-auto">
              Interaktivní časová osa ukazuje, jak spolu budeme pracovat od prvního setkání až po dlouhodobé partnerství
            </p>
          </div>

          {/* Desktop Timeline */}
          <div className="hidden lg:block mb-12">
            <div className="relative">
              <div className="absolute top-1/2 left-0 right-0 h-1 bg-border -translate-y-1/2"></div>
              <div className="relative flex justify-between">
                {phases.map((phase) => {
                  const isActive = activePhase === phase.id;
                  const colorClasses = getColorClasses(phase.color, isActive);
                  
                  return (
                    <button
                      key={phase.id}
                      onClick={() => setActivePhase(phase.id)}
                      className="flex flex-col items-center group relative z-10"
                    >
                      <div
                        className={`w-16 h-16 rounded-full ${colorClasses.bg} ${colorClasses.border} border-4 flex items-center justify-center mb-3 transition-smooth group-hover:scale-110 shadow-card`}
                      >
                        <Icon
                          name={phase.icon as any}
                          size={28}
                          className={colorClasses.text}
                        />
                      </div>
                      <span className={`text-sm font-semibold ${isActive ? 'text-text-primary' : 'text-text-secondary'} transition-smooth`}>
                        {phase.title}
                      </span>
                      <span className="text-xs text-text-secondary mt-1">{phase.timeframe}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Mobile Timeline */}
          <div className="lg:hidden mb-8 space-y-4">
            {phases.map((phase) => {
              const isActive = activePhase === phase.id;
              const colorClasses = getColorClasses(phase.color, isActive);
              
              return (
                <button
                  key={phase.id}
                  onClick={() => setActivePhase(phase.id)}
                  className={`w-full p-4 rounded-lg border-2 ${colorClasses.border} ${isActive ? colorClasses.bg : 'bg-card'} transition-smooth text-left`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-12 h-12 rounded-full ${isActive ? 'bg-white/20' : colorClasses.bg} flex items-center justify-center flex-shrink-0`}>
                      <Icon
                        name={phase.icon as any}
                        size={24}
                        className={isActive ? 'text-white' : colorClasses.text}
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className={`font-semibold ${isActive ? 'text-white' : 'text-text-primary'}`}>
                        {phase.title}
                      </h3>
                      <p className={`text-sm ${isActive ? 'text-white/80' : 'text-text-secondary'}`}>
                        {phase.timeframe}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Phase Details */}
          {phases.map((phase) => {
            if (activePhase !== phase.id) return null;
            const colorClasses = getColorClasses(phase.color, false);
            
            return (
              <div key={phase.id} className="bg-card rounded-xl shadow-elevated p-6 lg:p-8 border-2 border-border">
                <div className="flex items-start space-x-4 mb-6">
                  <div className={`w-16 h-16 rounded-xl ${colorClasses.bg} flex items-center justify-center flex-shrink-0`}>
                    <Icon
                      name={phase.icon as any}
                      size={32}
                      className={colorClasses.text}
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-text-primary mb-2 font-headline">
                      {phase.title}
                    </h3>
                    <p className="text-text-secondary">{phase.description}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {phase.activities.map((activity, index) => (
                    <div
                      key={index}
                      className="flex items-start space-x-3 p-4 bg-surface rounded-lg"
                    >
                      <Icon
                        name="CheckCircleIcon"
                        size={20}
                        className="text-success flex-shrink-0 mt-0.5"
                      />
                      <span className="text-sm text-text-primary">{activity}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default TimelineSection;